import { ReactNode, createContext, useContext, useMemo, useState, useCallback } from 'react';
import { useIsInvoiceAsyncProcessing } from 'src/use-cases/invoice/useInvoices';

export interface IPaymentRecalculationToastSelectorProps {
  children: ReactNode;
}

export interface IConfirmAsyncInvoiceArgs {
  invoiceId: string;
  deviationWorkflowAction: string;
  onCloseConfirm: () => void;
}

export interface IPaymentRecalculationToastValues {
  isLoading: boolean;
  isShowNotification: boolean;
  isShowSuccessInfo: boolean;
  currentInvoiceId: string | null;
  currentInvoiceNumber: string | null;
  currentShipmentNumber: string | null;
  confirmAsyncInvoice(props: IConfirmAsyncInvoiceArgs): Promise<void>;
  setIsShowSuccessInfo(value: boolean): void;
  setIsShowNotification(value: boolean): void;
  setCurrentInvoiceId(value: string): void;
  setCurrentInvoiceNumber(value: string): void;
  setCurrentShipmentNumber(value: string): void;
  closeToast(): void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const PaymentRecalculationToastDefaultValues = {
  isLoading: false,
  isShowNotification: false,
  isShowSuccessInfo: false,
  currentInvoiceId: null,
  currentInvoiceNumber: null,
  currentShipmentNumber: null,
  confirmAsyncInvoice: async () => {},
  setIsShowSuccessInfo: () => {},
  setIsShowNotification: () => {},
  setCurrentInvoiceId: () => {},
  setCurrentInvoiceNumber: () => {},
  setCurrentShipmentNumber: () => {},
  closeToast: () => {},
};

export const PaymentRecalculationToastSelectorContext = createContext<IPaymentRecalculationToastValues>(PaymentRecalculationToastDefaultValues);

export const PaymentRecalculationToastSelector = ({ children }: IPaymentRecalculationToastSelectorProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowNotification, setIsShowNotification] = useState<boolean>(false);
  const [isShowSuccessInfo, setIsShowSuccessInfo] = useState<boolean>(false);
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string>(null);
  const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState<string>(null);
  const [currentShipmentNumber, setCurrentShipmentNumber] = useState<string>(null);

  const { mutateAsync: isInvoiceAsyncProcessing } = useIsInvoiceAsyncProcessing();

  const confirmAsyncInvoice = useCallback(
    async (props: IConfirmAsyncInvoiceArgs) => {
      setIsLoading(true);
      setCurrentInvoiceId(props.invoiceId);

      const checkProcessingStatus = async () => {
        const isAsyncProcessing = await isInvoiceAsyncProcessing(props.invoiceId);

        if (isAsyncProcessing) {
          setTimeout(checkProcessingStatus, 1000); // Check again after 1 second
        } else {
          setIsShowSuccessInfo(true);
          setIsLoading(false);
        }
      };

      await checkProcessingStatus();
    },
    [isInvoiceAsyncProcessing]
  );

  const closeToast = useCallback(() => {
    setIsShowNotification(false);
    setCurrentInvoiceId(null);
  }, []);

  const value = useMemo(
    () => ({
      isLoading,
      isShowNotification,
      isShowSuccessInfo,
      setIsShowSuccessInfo,
      setIsShowNotification,
      currentInvoiceId,
      currentInvoiceNumber,
      currentShipmentNumber,
      setCurrentInvoiceId,
      setCurrentInvoiceNumber,
      setCurrentShipmentNumber,
      confirmAsyncInvoice,
      closeToast,
    }),
    [
      isLoading,
      isShowNotification,
      isShowSuccessInfo,
      setIsShowSuccessInfo,
      setIsShowNotification,
      currentInvoiceId,
      currentInvoiceNumber,
      currentShipmentNumber,
      setCurrentInvoiceId,
      setCurrentInvoiceNumber,
      setCurrentShipmentNumber,
      confirmAsyncInvoice,
      closeToast,
    ]
  );

  return <PaymentRecalculationToastSelectorContext.Provider value={value}>{children}</PaymentRecalculationToastSelectorContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export function usePaymentRecalculationToastSelector() {
  const context = useContext(PaymentRecalculationToastSelectorContext);

  if (!context) {
    throw new Error(`Payment Recalculation Toast selector compound components cannot be rendered outside the Payment Recalculation Toast Selector component`);
  }
  return context;
}
